<template>
  <article class="flex flex-col">
    <div class="mb-[120px] flex gap-[20px]">
      <div class="flex flex-col xl:w-3/4 2xl:w-4/5">
        <BaseTheContentBreadcrumbs :current-page-title="courseInfo?.title" />
        <h1
          class="mb-[32px] text-[32px] font-medium leading-[38px]"
          v-text="courseInfo?.title"
        />
        <p class="mb-[64px] text-[18px]" v-html="courseInfo?.announce" />
        <div class="page-body no-dynamic-format mb-[65px]">
          <CardsCourseContentCard
            v-for="(course, index) in data"
            :key="course.id"
            class="mb-[20px] last:mb-0 xl:mb-[80px]"
            state="audio"
            :data="course"
            :index="index"
            :course="currentCourse?.id === course.id ? currentCourse : null"
            @start-play="setCurrentCourse(course)"
            @end-play="isNextAvailableLectures(course.id)"
          />
        </div>
        <div v-if="courseInfo?.authors?.length" class="flex flex-col">
          <h3 class="mb-[36px] text-[24px] font-medium">Автор</h3>
          <div class="grid grid-flow-row gap-5">
            <CommonAuthorInfo
              v-for="author in courseInfo.authors"
              :key="author.id"
              :author="author"
            />
          </div>
        </div>
      </div>
      <CommonBlocksRightArticleBlock class="xl:w-1/4 2xl:w-1/5" />
    </div>
    <CommonBlocksPageFooterBlocks />
  </article>
</template>

<script setup>
definePageMeta({
  layout: 'marginless',
});

const route = useRoute();
const audioStore = useAudioStore();
const { secondsToISO } = helperSeo();

const slug = route?.params?.slug;

const { audioCourse } = useAudioCoursesBySlug(slug);
useErrorHandler(() => audioCourse.failureReason.value?.status);

const courseInfo = computed(() => audioCourse?.data?.value?.course);
const title = computed(() => courseInfo?.value?.title);
const data = computed(() => audioCourse?.data?.value?.data);

const robots = ref('all');
const seo = ref({});
const currentCourse = ref(null);

/** methods */
const setCurrentCourse = (course) => {
  currentCourse.value = course;
  audioStore.setCurrentCategoryList('courses');
  audioStore.setCurrentAudioList(data.value);
  audioStore.setCurrentAudioData(course);
  audioStore.setCurrent(course);
};

const isNextAvailableLectures = (id) => {
  const currentCourse = data.value?.findIndex((course) => course.id === id);

  if (currentCourse >= 0 && data.value.length - 1 - currentCourse) {
    setCurrentCourse(data.value[currentCourse + 1]);
  } else {
    setCurrentCourse(null);
  }
};

/** seo */
seo.value = audioCourse?.data?.value?.seo;
if (seo.value) {
  const arr = [];
  if (seo.value.index === false) arr.push('noindex');
  if (seo.value.follow === false) arr.push('nofollow');
  if (seo.value.archive === false) arr.push('noarchive');
  if (arr.length > 0) {
    robots.value = arr.reduce((previousValue, currentValue) => {
      return `${previousValue}, ${currentValue}`;
    });
  }
}

useHead(
  computed(() => ({
    title: `${
      title.value ? title.value + ' |' : ''
    } Слушать аудиокурсы по истории РФ для школьников и студентов`,
    meta: [
      {
        hid: 'og:type',
        property: 'og:type',
        content: 'music.song',
      },
      {
        hid: 'description',
        name: 'description',
        content: `${
          seo.value?.metadescription ?? audioCourse?.data?.value?.title ?? ''
        }. Слушать аудиокурсы по истории РФ для школьников и студентов на сайте Федерального портала истории России.`,
      },
      {
        property: 'og:image',
        content: `${useHostApp()}${courseInfo.value?.image?.src}`,
      },
      {
        hid: 'robots',
        name: 'robots',
        content: robots.value ?? '',
      },
      {
        property: 'og:title',
        content: `${
          title.value ? title.value + ' |' : ''
        } Слушать аудиокурсы по истории РФ для школьников и студентов`,
      },
      {
        property: 'og:url',
        content: `${useHostApp()}/listen/courses/${slug}`,
      },
      {
        property: 'og:description',
        content: `${
          seo.value?.metadescription ?? audioCourse?.data?.value?.title ?? ''
        }. Слушать аудиокурсы по истории РФ для школьников и студентов на сайте Федерального портала истории России.`,
      },
    ],
    link: [
      {
        rel: 'canonical',
        href: `${useHostApp()}${route.path}`,
      },
    ],
  })),
);

useSchemaOrg(() => [
  defineArticle({
    image: {
      '@type': 'ImageObject',
      url: `${useHostApp()}${courseInfo.value?.image?.src}`,
      width: 800,
      height: 600,
      caption: courseInfo.value?.image?.alt,
    },
    author: {
      name: 'history.ru',
      url: `${useHostApp()}`,
      sameAs: '',
      description: '',
    },
    ...data.value?.map((audio) => ({
      '@type': 'AudioObject',
      duration: secondsToISO(audio?.metadata?.duration),
      bitrate: `${(audio.metadata?.bitrate / 1000)?.toFixed(0)} kbps`,
      contentUrl: `${useHostApp()}${audio?.path}`,
      thumbnailUrl: `${useHostApp()}${audio?.image?.src}`,
    })),
  }),
]);
</script>
